import { supabase } from '@/integrations/supabase/client';
import { ApiResult, ok, err } from '../errors';
import type { Json } from '@/integrations/supabase/types';

/**
 * Product database model - matches actual database schema
 */
export interface Product {
  id: string;
  brand_id: string | null;
  name: string;
  slug: string;
  is_active: boolean | null;
  is_template: boolean | null;
  branding: Json | null;
  config: Json | null;
  modules: Json | null;
  tracking_metrics: Json | null;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * Product configuration structure
 */
export interface ProductConfig {
  features: {
    programs?: boolean;
    tracking?: boolean;
    community?: boolean;
    assessments?: boolean;
    chat?: boolean;
    library?: boolean;
    schedule?: boolean;
    gamification?: boolean;
  };
  trackingMetrics: string[];
  dashboardWidgets: string[];
  defaultProgram?: string;
  onboardingFlow?: string[];
}

/**
 * Product branding configuration
 */
export interface ProductBranding {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  logo?: string;
  tagline?: string;
}

/**
 * Input for creating a new product
 */
export interface CreateProductInput {
  brand_id: string;
  name: string;
  slug: string;
  description?: string;
  branding: ProductBranding;
  config?: Partial<ProductConfig>;
}

/**
 * Input for updating a product
 */
export interface UpdateProductInput {
  name?: string;
  slug?: string;
  is_active?: boolean;
  branding?: Json;
  config?: Json;
  modules?: Json;
  tracking_metrics?: Json;
}

/**
 * Get all products for a brand
 */
export async function getBrandProducts(brandId: string): Promise<ApiResult<Product[]>> {
  try {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('brand_id', brandId)
      .order('name');

    if (error) throw error;

    return ok((data || []) as Product[]);
  } catch (error: any) {
    console.error('Failed to fetch brand products:', error);
    return err(error.message || 'Failed to fetch products');
  }
}

/**
 * Get active products for a brand
 */
export async function getActiveBrandProducts(brandId: string): Promise<ApiResult<Product[]>> {
  try {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('brand_id', brandId)
      .eq('is_active', true)
      .order('name');

    if (error) throw error;

    return ok((data || []) as Product[]);
  } catch (error: any) {
    console.error('Failed to fetch active brand products:', error);
    return err(error.message || 'Failed to fetch active products');
  }
}

/**
 * Get product by ID
 */
export async function getProductById(productId: string): Promise<ApiResult<Product | null>> {
  try {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('id', productId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return ok(null);
      }
      throw error;
    }

    return ok(data as Product);
  } catch (error: any) {
    console.error('Failed to fetch product:', error);
    return err(error.message || 'Failed to fetch product');
  }
}

/**
 * Get product by slug (within a brand)
 */
export async function getProductBySlug(
  brandId: string,
  slug: string
): Promise<ApiResult<Product | null>> {
  try {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('brand_id', brandId)
      .eq('slug', slug)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return ok(null);
      }
      throw error;
    }

    return ok(data as Product);
  } catch (error: any) {
    console.error('Failed to fetch product by slug:', error);
    return err(error.message || 'Failed to fetch product');
  }
}

/**
 * Create a new product
 */
export async function createProduct(input: CreateProductInput): Promise<ApiResult<Product>> {
  try {
    // Generate product ID from slug
    const productId = generateProductId(input.slug);

    // Build product data
    const productData = {
      id: productId,
      brand_id: input.brand_id,
      name: input.name,
      slug: input.slug,
      is_active: true,
      branding: input.branding as unknown as Json,
      config: buildDefaultConfig(input.config) as unknown as Json,
    };

    // Insert product
    const { data, error } = await supabase
      .from('products')
      .insert([productData])
      .select()
      .single();

    if (error) throw error;

    // Create default content for the product
    await createDefaultProductContent(data.id);

    return ok(data as Product);
  } catch (error: any) {
    console.error('Failed to create product:', error);
    return err(error.message || 'Failed to create product');
  }
}

/**
 * Update a product
 */
export async function updateProduct(
  productId: string,
  input: UpdateProductInput
): Promise<ApiResult<Product>> {
  try {
    const { data, error } = await supabase
      .from('products')
      .update(input)
      .eq('id', productId)
      .select()
      .single();

    if (error) throw error;

    return ok(data as Product);
  } catch (error: any) {
    console.error('Failed to update product:', error);
    return err(error.message || 'Failed to update product');
  }
}

/**
 * Delete a product (soft delete by setting is_active to false)
 */
export async function deleteProduct(productId: string): Promise<ApiResult<void>> {
  try {
    const { error } = await supabase
      .from('products')
      .update({ is_active: false })
      .eq('id', productId);

    if (error) throw error;

    return ok(undefined);
  } catch (error: any) {
    console.error('Failed to delete product:', error);
    return err(error.message || 'Failed to delete product');
  }
}

/**
 * Get user's enrolled products
 */
export async function getUserEnrolledProducts(userId: string): Promise<ApiResult<Product[]>> {
  try {
    const { data, error } = await supabase
      .from('user_product_enrollments')
      .select('product_id, products(*)')
      .eq('user_id', userId)
      .eq('is_active', true);

    if (error) throw error;

    // Extract products from enrollment data
    const products = data
      ?.map((enrollment: any) => enrollment.products)
      .filter(Boolean) || [];

    return ok(products as Product[]);
  } catch (error: any) {
    console.error('Failed to fetch user enrolled products:', error);
    return err(error.message || 'Failed to fetch enrolled products');
  }
}

/**
 * Get products available to user based on their brand enrollments
 */
export async function getUserAvailableProducts(userId: string): Promise<ApiResult<Product[]>> {
  try {
    // Get user's brand enrollments
    const { data: brandUsers, error: brandError } = await supabase
      .from('brand_users')
      .select('brand_id')
      .eq('user_id', userId);

    if (brandError) throw brandError;

    if (!brandUsers || brandUsers.length === 0) {
      return ok([]);
    }

    // Get products for those brands
    const brandIds = brandUsers.map(bu => bu.brand_id);
    const { data: products, error: productsError } = await supabase
      .from('products')
      .select('*')
      .in('brand_id', brandIds)
      .eq('is_active', true)
      .order('name');

    if (productsError) throw productsError;

    return ok((products || []) as Product[]);
  } catch (error: any) {
    console.error('Failed to fetch user available products:', error);
    return err(error.message || 'Failed to fetch available products');
  }
}

/**
 * Enroll user in a product
 */
export async function enrollUserInProduct(
  userId: string,
  productId: string
): Promise<ApiResult<void>> {
  try {
    const { error } = await supabase
      .from('user_product_enrollments')
      .upsert({
        user_id: userId,
        product_id: productId,
        is_active: true,
        enrolled_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id,product_id'
      });

    if (error) throw error;

    return ok(undefined);
  } catch (error: any) {
    console.error('Failed to enroll user in product:', error);
    return err(error.message || 'Failed to enroll user');
  }
}

// ============ Helper Functions ============

/**
 * Generate product ID from slug
 */
function generateProductId(slug: string): string {
  return slug.toLowerCase().replace(/[^a-z0-9-]/g, '-');
}

/**
 * Build default product configuration
 */
function buildDefaultConfig(customConfig?: Partial<ProductConfig>): ProductConfig {
  return {
    features: {
      programs: true,
      tracking: true,
      community: false,
      assessments: true,
      chat: true,
      library: true,
      schedule: true,
      gamification: false,
      ...customConfig?.features,
    },
    trackingMetrics: customConfig?.trackingMetrics || [
      'daily_mood',
      'stress_level',
      'sleep_quality',
    ],
    dashboardWidgets: customConfig?.dashboardWidgets || [
      'progress_overview',
      'daily_checkin',
      'upcoming_sessions',
      'recent_content',
    ],
    defaultProgram: customConfig?.defaultProgram,
    onboardingFlow: customConfig?.onboardingFlow || [
      'welcome',
      'profile_setup',
      'assessment',
      'program_selection',
    ],
  };
}

/**
 * Create default content for a new product
 *
 * This creates placeholder content like a default program, welcome message, etc.
 */
async function createDefaultProductContent(productId: string): Promise<void> {
  try {
    // Note: This is a placeholder implementation
    // In a real scenario, you might want to:
    // 1. Create a default program
    // 2. Create welcome messages
    // 3. Set up default assessment
    // 4. Create sample library content

    console.log(`Creating default content for product: ${productId}`);

    // For now, we'll just log. This can be expanded later.
    // You could call other API services here to create:
    // - await createDefaultProgram(productId)
    // - await createWelcomeMessages(productId)
    // - await createDefaultAssessment(productId)

  } catch (error) {
    console.error('Failed to create default product content:', error);
    // Don't throw - we don't want to block product creation if content creation fails
  }
}
